import { useUser as useClerkUser, useAuth } from "@clerk/nextjs";
import { api } from "@/trpc/react";
import { useEffect, useMemo } from "react";

export function useUser() {
  const { user: clerkUser, isSignedIn, isLoaded } = useClerkUser();
  const { sessionId } = useAuth();
  const utils = api.useUtils();

  const clerkId = clerkUser?.id;
  const primaryEmail = clerkUser?.emailAddresses[0]?.emailAddress ?? null;
  const firstName = clerkUser?.firstName ?? null;
  const lastName = clerkUser?.lastName ?? null;
  const imageUrl = clerkUser?.imageUrl ?? null;
  // Get user data from our database only if signed in
  const { data: dbUser, isLoading: isLoadingUser } = api.users.me.useQuery(
    undefined,
    {
      enabled: isLoaded && isSignedIn, // Only run query when Clerk is loaded AND user is signed in
      retry: false, // Don't retry if it fails
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on component mount
    },
  );

  // Sync mutation
  const { mutate: syncUser } = api.users.sync.useMutation({
    onSuccess: () => {
      // Refresh user data after sync
      utils.users.me.invalidate();
    },
  });

  const syncPayload = useMemo(
    () =>
      clerkId
        ? {
            id: clerkId,
            email: primaryEmail,
            firstName,
            lastName,
            imageUrl,
          }
        : null,
    [clerkId, primaryEmail, firstName, lastName, imageUrl],
  );

  // Sync user data when they sign in — at most once per Clerk session.
  // GUARD: if sessionId is null/undefined (Clerk still initializing), fall through to allow
  // a possible single fire; the server upsert is idempotent so double-fire is safe.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !syncPayload) return;
    if (sessionId) {
      const key = `user-synced:${sessionId}`;
      if (sessionStorage.getItem(key)) return;
      syncUser(syncPayload, {
        onSuccess: () => {
          sessionStorage.setItem(key, '1');
        },
      });
    } else {
      syncUser(syncPayload);
    }
  }, [isLoaded, isSignedIn, syncPayload, syncUser, sessionId]);

  return {
    user: dbUser, // Our database user with permissions
    isLoading: !isLoaded || (isSignedIn && isLoadingUser), // Only consider loading if signed in and fetching
    isSignedIn,
    hasAccessVideos: dbUser?.hasAccessVideos ?? false,
    isAuthor: dbUser?.isAuthor ?? false,
  };
}
